// How the product photos in the preview's newest answer look, as the preview frame reports them
// (services/storefront-assistant/widget-ui/frame.js, photoFacts): untrusted page data, bounded here.
// The canvas shows a one-line check from them, and "Fix with Chat" hands them to Chat.
export interface PhotoFact {
  name: string;
  photo: boolean;
  size?: [number, number];
  tile?: [number, number];
  fit?: 'contain' | 'cover';
  /** How much of the room inside its tile (inside the padding) the whole photo fills (contain), 0–1. */
  fill?: number;
  /** How much of the photo is cut (cover), 0–1. */
  cut?: number;
  /** Screen pixels per photo pixel on a phone: above 1 it is shown larger than its own pixels. */
  enlarged?: number;
}

/** A photo filling less than this of the room in its tile is far from the tile's shape and looks small. */
export const SMALL_FILL = 0.45;
/** Cut by more than this, a photo loses part of the product. */
export const CUT = 0.05;
/** Enlarged beyond this on a phone, a photo looks soft. */
export const SOFT = 2;

const share = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1
    ? value
    : undefined;
const pair = (value: unknown): [number, number] | undefined =>
  Array.isArray(value) &&
  value.length === 2 &&
  value.every((n) => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 20_000)
    ? [Math.round(value[0]), Math.round(value[1])]
    : undefined;

/** The report, bounded: at most 12 photos, numbers in range, names clipped. Null when it is not one. */
export function readPhotoFacts(value: unknown): PhotoFact[] | null {
  if (!Array.isArray(value) || !value.length || value.length > 12) return null;
  const facts: PhotoFact[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') return null;
    const raw = entry as Record<string, unknown>;
    const name =
      typeof raw.name === 'string' ? raw.name.replace(/\s+/g, ' ').trim().slice(0, 80) : '';
    if (raw.photo !== true) {
      facts.push({ name, photo: false });
      continue;
    }
    const enlarged =
      typeof raw.enlarged === 'number' &&
      Number.isFinite(raw.enlarged) &&
      raw.enlarged >= 0 &&
      raw.enlarged <= 50
        ? raw.enlarged
        : undefined;
    facts.push({
      name,
      photo: true,
      size: pair(raw.size),
      tile: pair(raw.tile),
      fit: raw.fit === 'cover' ? 'cover' : 'contain',
      fill: share(raw.fill),
      cut: share(raw.cut),
      enlarged,
    });
  }
  return facts;
}

export interface PhotoSummary {
  total: number;
  small: number;
  cut: number;
  soft: number;
  missing: number;
}

export function summarizePhotos(facts: PhotoFact[]): PhotoSummary {
  const shown = facts.filter((fact) => fact.photo);
  return {
    total: facts.length,
    small: shown.filter((fact) => fact.fit !== 'cover' && (fact.fill ?? 1) < SMALL_FILL).length,
    cut: shown.filter((fact) => (fact.cut ?? 0) > CUT).length,
    soft: shown.filter((fact) => (fact.enlarged ?? 0) > SOFT).length,
    missing: facts.length - shown.length,
  };
}

export const photosLookRight = (summary: PhotoSummary) =>
  !summary.small && !summary.cut && !summary.soft && !summary.missing;

/** What the design can change: the tile's shape and room, the fit, the layout. A missing photo is the store's. */
export const photosFixable = (summary: PhotoSummary) =>
  summary.small > 0 || summary.cut > 0 || summary.soft > 0;
